import { FC } from "react";

import { Stack } from "@mui/material";
import { TicketingFormCategory } from "@simplyk/common";
import { useFormContext } from "react-hook-form";

import { RateOption } from "../types";

import { FormValues } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAutocompleteV2 } from "@/dashboard-design-system/DashboardAutocompleteV2";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";

interface RateAssignmentSectionProps {
  rates: RateOption[];
  formCategory?: TicketingFormCategory;
}

// Who a guest question is asked to: every ticket type, or the ones picked here. Submitting with the
// toggle off and nothing picked would ask it to nobody, and the API refuses that, so the picker does.
export const RateAssignmentSection: FC<RateAssignmentSectionProps> = ({ rates, formCategory }) => {
  const { t } = useTranslate();
  const form = useFormContext<FormValues>();
  const askForAllRates = form.watch("askForAllRates");
  const isShop = formCategory === TicketingFormCategory.Shop;
  const isSweepstakes = formCategory === TicketingFormCategory.Sweepstakes;

  return (
    <Stack spacing={1}>
      <DashboardSwitch
        name="askForAllRates"
        label={
          isShop
            ? t("dashboard", "askForAllItemTypes")
            : isSweepstakes
              ? t("dashboard", "sweepstakes.questions.askForAllEntryTypes")
              : t("dashboard", "askForAllTicketTypes")
        }
        checked={askForAllRates}
        onChange={(_, checked) => form.setValue("askForAllRates", checked)}
      />

      {!askForAllRates && (
        <DashboardAutocompleteV2
          placeholder={t(
            "dashboard",
            isSweepstakes ? "sweepstakes.questions.selectEntryRates" : "donationForm.questionAssignedThisTickets"
          )}
          control={form.control}
          name="ratesAssigned"
          options={rates}
          multiple
          limitTags={2}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          // The field stays registered after this section unmounts, so the rule has to re-read the two
          // states that make it irrelevant: a buyer question, or one asked for every ticket type.
          rules={[
            {
              validate: (value: RateOption[] | null | undefined) =>
                form.getValues("scope") !== "participant" ||
                form.getValues("askForAllRates") ||
                (value?.length ?? 0) > 0 ||
                (isSweepstakes
                  ? t("dashboard", "sweepstakes.questions.selectAtLeastOneEntryRate")
                  : t(
                      "dashboard",
                      isShop
                        ? "stepQuestionsV4.builder.selectAtLeastOneItemType"
                        : "stepQuestionsV4.builder.selectAtLeastOneTicketType"
                    )),
            },
          ]}
          error={Boolean(form.formState.errors.ratesAssigned)}
          errorMessage={form.formState.errors.ratesAssigned?.message}
        />
      )}
    </Stack>
  );
};
